import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { Character } from "@/lib/api";
import { CharacterAvatar } from "@/CharacterAvatar";
import { MessagesSquare, X } from "lucide-react";

export function NewRoomModal({
  chars,
  name,
  scenario,
  pick,
  onName,
  onScenario,
  onPick,
  onCancel,
  onCreate
}: {
  chars: Character[];
  name: string;
  scenario: string;
  pick: Set<number>;
  onName: (v: string) => void;
  onScenario: (v: string) => void;
  onPick: (v: Set<number>) => void;
  onCancel: () => void;
  onCreate: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onCancel();
    }}>
      <div role="dialog" aria-modal="true" aria-labelledby="new-room-title" className="w-full max-w-md rounded-t-2xl border bg-card p-4 shadow-2xl sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="new-room-title" className="flex items-center gap-2 font-semibold"><MessagesSquare size={18} className="text-primary" />New room</h2>
            <p className="mt-1 text-xs text-muted-foreground">Choose a name and add the characters you want in the room.</p>
          </div>
          <Button aria-label="Close dialog" variant="ghost" size="icon" onClick={onCancel}><X /></Button>
        </div>
        <Input
          placeholder="Room name…"
          value={name}
          onChange={(e) => onName(e.target.value)}
        />
        <label className="mt-3 block text-xs text-muted-foreground">
          Shared scenario <span className="text-muted-foreground/70">(optional)</span>
          <Textarea
            placeholder="Where are you, what is happening, and what world rules apply?"
            value={scenario}
            onChange={(event) => onScenario(event.target.value)}
            className="mt-1 min-h-20"
          />
        </label>
        <p className="mt-4 text-xs font-medium text-muted-foreground">Cast</p>
        <div className="mt-2 max-h-52 space-y-1 overflow-y-auto rounded-lg border border-border bg-background/40 p-2">
          {chars.map((c) => (
            <label key={c.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/60">
              <input
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                type="checkbox"
                checked={pick.has(c.id!)}
                onChange={(e) => {
                  const v = new Set(pick);
                  if (e.target.checked) v.add(c.id!);
                  else v.delete(c.id!);
                  onPick(v);
                }}
              />
              <CharacterAvatar character={c} className="h-6 w-6 text-xs" />
              <span style={{ color: c.color }}>{c.name}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={onCreate}>Create room</Button>
        </div>
      </div>
    </div>
  );
}